import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import Page from '../components/Page';
import Crumbs from '../components/Crumbs';
import Pill from '../components/Pill';
import { fmt, weiToEth, short } from '../lib/format';
import { useAccount } from '../hooks/useWallet';
import { useReadContracts } from '../hooks/useContracts';
import { useBalanceOf, useTotalSupply } from '../hooks/useToken';
import {
  useAccountClaimableTokens,
  useAccountTotalContribution,
  useCurrentPeriod,
  useAccountCurrentPeriodContribution,
  useUnclaimedPeriods,
  useWeiContributionPerPeriod,
} from '../hooks/useCrowdsale';
import { useRequestCount } from '../hooks/useAuditingPoolConfig';
import { useRequestDepositOf, type RequestRow } from '../hooks/useRequests';
import { useIsActiveAuditor } from '../hooks/useAuditorsInPool';
import { useWriteContract } from '../hooks/useWriteContract';
import {
  useFirmIdOf,
  useFirm,
  FIRM_STATUSES,
  SENIORITY_LEVELS,
  NOT_FOUND,
} from '../hooks/useAuditorRegistry';

export default function Account() {
  const account = useAccount();
  const contracts = useReadContracts();

  const balance      = useBalanceOf(account);
  const supply       = useTotalSupply();
  const claimable    = useAccountClaimableTokens(account);
  const totalContrib = useAccountTotalContribution(account);
  const currentP     = useCurrentPeriod();
  const currentContrib = useAccountCurrentPeriodContribution(account);
  const reqCount     = useRequestCount();
  const isActive     = useIsActiveAuditor(account);
  const myFirmId     = useFirmIdOf(account);
  const myFirmKnown  = myFirmId.data !== undefined && myFirmId.data !== NOT_FOUND;
  const myFirm       = useFirm(myFirmKnown ? myFirmId.data : null);
  const myReqDep     = useRequestDepositOf(account);

  const balanceMeth      = balance.data !== undefined ? weiToEth(balance.data) : 0;
  const supplyMeth       = supply.data !== undefined ? weiToEth(supply.data) : 0;
  const votePct          = supplyMeth > 0 ? (balanceMeth / supplyMeth) * 100 : 0;
  const claimableMeth    = claimable.data !== undefined ? weiToEth(claimable.data) : 0;
  const totalContribEth  = totalContrib.data !== undefined ? weiToEth(totalContrib.data) : 0;
  const currentContribEth = currentContrib.data !== undefined ? weiToEth(currentContrib.data) : 0;
  const myStakeMeth      = myFirm.data !== undefined ? weiToEth(myFirm.data.stake) : 0;
  const myFirmStatusName = myFirm.data !== undefined ? FIRM_STATUSES[Number(myFirm.data.status)] : null;
  const myReqDepEth      = myReqDep.data !== undefined ? weiToEth(myReqDep.data) : 0;

  // Find user's lab requests (filter requestList by labWallet)
  const N = reqCount.data !== undefined ? Number(reqCount.data) : 0;
  const reqQueries = useQueries({
    queries: account
      ? Array.from({ length: N }, (_, i) => ({
          queryKey: ['metha', 'auditingPool', 'requestList', i] as const,
          queryFn: async () => {
            const row = (await contracts.auditingPool.requestList(BigInt(i))) as unknown as RequestRow;
            return row;
          },
          staleTime: 30_000,
        }))
      : [],
  });

  const myRequests = useMemo(() => {
    if (!account) return [];
    return reqQueries
      .map((q, i) => (q.data ? { id: i, row: q.data } : null))
      .filter((x): x is { id: number; row: RequestRow } => x !== null)
      .filter(({ row }) => row.labWallet.toLowerCase() === account.toLowerCase());
  }, [reqQueries, account]);

  // Derive role pills
  const roles: ReactNode[] = [];
  if (account && totalContrib.data !== undefined && totalContrib.data > 0n) roles.push(<Pill key="c" tone="ctx">Contributor</Pill>);
  if (account && myRequests.length > 0) roles.push(<Pill key="l" tone="blue">Lab ({myRequests.length} req)</Pill>);
  if (account && isActive.data) roles.push(<Pill key="a" tone="purple">Active auditor</Pill>);
  if (account && balance.data !== undefined && balance.data > 0n) roles.push(<Pill key="h" tone="ctx">Holder</Pill>);
  if (account && roles.length === 0) roles.push(<Pill key="n" tone="gray">No on-chain activity yet</Pill>);

  const auditorStateLabel =
    !account              ? 'connect wallet' :
    isActive.data         ? 'active firm' :
    myFirmKnown           ? `registered · ${myFirmStatusName?.toLowerCase()}` :
                            'not registered';

  return (
    <Page ctxLabel="account" title="Metha · Account" docsPath="guides/holder" docsLabel="Holder guide">
      <Crumbs crumbs={[{ to: '/', label: 'Overview' }, { label: 'Account' }]} />

      <div className="ph">
        <div>
          <h1>Account <span className="acc">/ {account ? short(account) : 'not connected'}</span></h1>
          <p className="lede">
            Your wallet across the protocol. You can hold multiple roles
            simultaneously — contributor + lab + auditor + holder are not mutually exclusive.
          </p>
        </div>
        <div className="meta">
          <span>WALLET <b>{account ? short(account) : '—'}</b></span>
          <span>NETWORK <b>Sepolia</b></span>
          <span>AUDITOR <b>{auditorStateLabel}</b></span>
        </div>
      </div>

      <div className="row r-2u1 mb-14">
        <div className="panel">
          <div className="p-head"><h3>Identity</h3><span className="src">on-chain · Supabase enrichment Phase 2.5</span></div>
          <div className="p-body" style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
            <span className="av" style={{ width: 64, height: 64, fontSize: 22, background: 'var(--ctx)' }}>
              {account ? account.slice(2, 4).toUpperCase() : '?'}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 18, fontWeight: 600 }}>{account ? short(account) : 'Wallet not connected'}</div>
              <div className="mono dim mt-14" style={{ fontSize: 11, letterSpacing: 0.5, lineHeight: 1.7 }}>
                wallet · <span className="acc">{account ?? '—'}</span><br />
                <span className="dim">ENS resolution + Supabase profile come in Phase 2.5.</span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
              {roles}
            </div>
          </div>
        </div>

        <div className="panel">
          <div className="p-head"><h3>Holdings</h3><span className="src"><code>token.balanceOf(you)</code></span></div>
          <div className="big-stat">
            <div className="l">METH BALANCE</div>
            <div className="v">{fmt.meth(balanceMeth)}<span className="u">METH</span></div>
            <div className="d">vote weight {votePct.toFixed(2)}%</div>
          </div>
          <div style={{ padding: '0 18px 14px' }}>
            <Link to="/governance" className={`btn btn-block${balanceMeth === 0 ? ' btn-locked' : ''}`}>
              {balanceMeth > 0 ? 'Use voting power →' : 'Acquire METH first'}
            </Link>
          </div>
        </div>
      </div>

      <div className="kpi-row mb-24" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
        <KpiLink to="/auction" l="CLAIMABLE METH" v={<>{fmt.meth(claimableMeth)}<span className="u">METH</span></>}
                 d={claimableMeth > 0 ? 'unclaimed periods · claim →' : 'nothing to claim'}
                 tone={claimableMeth > 0 ? 'up' : undefined} />
        <KpiLink to="/auditing" l="MY REQUESTS"
                 v={<>{myRequests.length}<span className="u">active</span></>}
                 d={myRequests.length > 0 ? `${myRequests.length} on-chain` : 'submit one →'} />
        <KpiLink to="/auction" l="TOTAL CONTRIBUTED" v={<>{totalContribEth.toFixed(4)}<span className="u">ETH</span></>}
                 d={`current period: ${currentContribEth.toFixed(4)} ETH`} />
        <KpiLink to="/auditors" l="AUDITOR STATUS"
                 v={<span style={{ fontSize: 14, color: isActive.data ? 'var(--green)' : 'var(--dim)' }}>{auditorStateLabel}</span>}
                 d={myFirmKnown ? `firm #${String(myFirmId.data)} · ${fmt.meth(myStakeMeth)} METH staked` : 'register firm →'} />
      </div>

      <div className="row r-2u1 mb-14">
        <div className="panel">
          <div className="p-head"><h3>My requests · lab</h3><span className="src">filter requestList where labWallet=you</span></div>
          {!account && (
            <div style={{ padding: 18, color: 'var(--dim)', fontFamily: 'var(--mono)', fontSize: 12 }}>
              Connect your wallet to see your research requests.
            </div>
          )}
          {account && myRequests.length === 0 && (
            <div style={{ padding: 18, color: 'var(--dim)', fontFamily: 'var(--mono)', fontSize: 12 }}>
              You haven't submitted any research requests yet.{' '}
              <Link to="/request/new" className="acc">Submit your first →</Link>
            </div>
          )}
          {myRequests.length > 0 && (
            <div className="tbl">
              <div className="th" style={{ gridTemplateColumns: '60px 1fr 100px 100px' }}>
                <span>ID</span><span>TITLE</span><span>HYPE</span><span>STATUS</span>
              </div>
              {myRequests.map(({ id, row }) => {
                const s =
                  row.cancelled ? { tone: 'red' as const, label: 'Cancelled' } :
                  !row.verdictCast ? { tone: 'blue' as const, label: 'Voting' } :
                  row.verdictAccepted ? { tone: 'green' as const, label: 'Approved' } :
                  { tone: 'red' as const, label: 'Rejected' };
                return (
                  <Link key={id} to={`/request/${id}`} className="tr"
                        style={{ gridTemplateColumns: '60px 1fr 100px 100px', textDecoration: 'none', fontSize: 12 }}>
                    <span className="pid">#{String(id).padStart(3, '0')}</span>
                    <span className="who" style={{ fontSize: 13 }}>{row.title || '(untitled)'}
                      <small>currentMilestone {Number(row.currentMilestone)}</small></span>
                    <span>↑ {fmt.meth(weiToEth(row.hype))}</span>
                    <span><Pill tone={s.tone}>{s.label}</Pill></span>
                  </Link>
                );
              })}
            </div>
          )}
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--line)' }}>
            <Link to="/request/new" className="btn btn-prim btn-block">+ New request</Link>
          </div>
        </div>

        <aside>
          <div className="panel mb-14">
            <div className="p-head"><h3>Deposits & stake</h3><span className="src">on-chain</span></div>
            <div className="p-body">
              <div className="estim"><span>REQUEST DEPOSIT</span><b>{account ? `${myReqDepEth} ETH` : '—'}</b></div>
              <div className="estim"><span>FIRM STAKE (METH)</span><b>{myFirmKnown ? `${fmt.meth(myStakeMeth)} METH` : '—'}</b></div>
              <div className="estim"><span>FIRM ID</span><b>{myFirmKnown ? `#${String(myFirmId.data)}` : '—'}</b></div>
            </div>
          </div>

          {account && myFirmKnown && myFirm.data && (
            <div className="panel mb-14" style={{ borderColor: 'var(--purple)' }}>
              <div className="p-head" style={{ background: 'rgba(169,120,255,.04)', borderColor: 'var(--purple)' }}>
                <h3 style={{ color: 'var(--purple)' }}>Your auditing firm</h3>
                <span className="src" style={{ color: 'var(--purple)' }}><code>registry.getFirm(#{String(myFirmId.data)})</code></span>
              </div>
              <div className="p-body">
                <div className="estim"><span>NAME</span><b>{myFirm.data.name}</b></div>
                <div className="estim"><span>JURISDICTION</span><b>{myFirm.data.jurisdiction}</b></div>
                <div className="estim"><span>SENIORITY</span><b>{SENIORITY_LEVELS[Number(myFirm.data.seniority)]}</b></div>
                <div className="estim"><span>STATUS</span><b style={{ color: myFirmStatusName === 'Active' ? 'var(--green)' : myFirmStatusName === 'Banned' ? 'var(--red)' : 'var(--amber)' }}>{myFirmStatusName}</b></div>
                <div className="estim"><span>REPUTATION</span><b>{Number(myFirm.data.reputation)} / 300</b></div>
                <div className="estim"><span>DSMB VETO</span><b>{myFirm.data.dsmbVetoRights ? 'YES' : '—'}</b></div>
                <Link to="/auditors" className="btn btn-block mt-14">Manage firm →</Link>
              </div>
            </div>
          )}

          <div className="panel">
            <div className="p-head"><h3>Crowdsale stats</h3><span className="src">your participation</span></div>
            <div className="p-body">
              <div className="estim"><span>TOTAL CONTRIBUTED</span><b>{totalContribEth.toFixed(4)} ETH</b></div>
              <div className="estim"><span>CURRENT PERIOD</span><b>#{currentP.data !== undefined ? Number(currentP.data) : '—'}</b></div>
              <div className="estim"><span>YOUR CONTRIB · NOW</span><b>{currentContribEth.toFixed(4)} ETH</b></div>
              <div className="estim"><span>UNCLAIMED METH</span><b>{fmt.meth(claimableMeth)}</b></div>
              <ClaimAllButton account={account} claimableMeth={claimableMeth} />
              <p className="help mt-14" style={{ marginBottom: 0 }}>
                "Claim all" walks every unclaimed period in one tx — cheap when
                you only have a handful, but can hit gas limits past ~50
                periods. The
                <b style={{ color: 'var(--ink)' }}> panel below </b>
                lets you cherry-pick or claim a range so the tx stays bounded.
              </p>
            </div>
          </div>
        </aside>
      </div>

      <GranularClaimPanel account={account} />
    </Page>
  );
}

function ClaimAllButton({ account, claimableMeth }: { account: string | null; claimableMeth: number }) {
  const { write, status } = useWriteContract();
  const submitting = status === 'submitting' || status === 'pending';
  const disabled = !account || claimableMeth === 0 || submitting;
  return (
    <button
      className={`btn btn-prim btn-block mt-14${disabled ? ' btn-locked' : ''}`}
      disabled={disabled}
      onClick={async () => {
        await write({
          contract: 'crowdsale',
          fn: 'claimAll',
          args: [],
          options: {
            pendingTitle: `Claiming ${fmt.meth(claimableMeth)} METH…`,
            successTitle: `Claimed ${fmt.meth(claimableMeth)} METH`,
            invalidate: [['metha', 'crowdsale'], ['metha', 'token']],
          },
        });
      }}
    >
      {submitting ? 'Claiming…' : claimableMeth > 0 ? `Claim all (${fmt.meth(claimableMeth)} METH)` : 'Nothing to claim'}
    </button>
  );
}

/* -------------------- Granular claim panel -------------------- */

/**
 * Lists every closed period the user hasn't claimed yet and lets them
 * cherry-pick which to claim. The action bar picks the cheapest fn
 * automatically:
 *   - 1 period selected   → claimPeriod(id)
 *   - contiguous range    → claimRange(from, to)
 *   - sparse selection    → claimList([ids])
 * This dodges the gas-out risk of claimAll when the user has many periods.
 */
function GranularClaimPanel({ account }: { account: string | null }) {
  const unclaimed = useUnclaimedPeriods(account);
  const periods = unclaimed.data ?? [];
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Range-claim form (independent of selection): from/to inputs let the user
  // claim a range that may extend beyond what the unclaimed walk surfaced.
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');

  const { write, status } = useWriteContract();
  const submitting = status === 'submitting' || status === 'pending';

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function selectAll() { setSelected(new Set(periods)); }
  function selectNone() { setSelected(new Set()); }

  // Pick the cheapest contract call based on the shape of the selection.
  const selectedSorted = useMemo(() => [...selected].sort((a, b) => a - b), [selected]);
  const isContiguous = selectedSorted.length > 1 &&
    selectedSorted.every((id, i) => i === 0 || id === selectedSorted[i - 1] + 1);
  const optimalCall: { fn: string; args: unknown[] } | null = (() => {
    if (selectedSorted.length === 0) return null;
    if (selectedSorted.length === 1) {
      return { fn: 'claimPeriod', args: [BigInt(selectedSorted[0])] };
    }
    if (isContiguous) {
      return { fn: 'claimRange', args: [BigInt(selectedSorted[0]), BigInt(selectedSorted[selectedSorted.length - 1])] };
    }
    return { fn: 'claimList', args: [selectedSorted.map((p) => BigInt(p))] };
  })();

  async function doClaimSelected() {
    if (!optimalCall || submitting || !account) return;
    const count = selectedSorted.length;
    await write({
      contract: 'crowdsale',
      fn: optimalCall.fn,
      args: optimalCall.args,
      options: {
        pendingTitle: `Claiming ${count} period${count === 1 ? '' : 's'} (${optimalCall.fn})…`,
        successTitle: `Claimed ${count} period${count === 1 ? '' : 's'}`,
        invalidate: [['metha', 'crowdsale'], ['metha', 'token']],
      },
    });
    setSelected(new Set());
  }

  async function doClaimRange() {
    if (submitting || !account) return;
    const from = parseInt(rangeFrom, 10);
    const to = parseInt(rangeTo, 10);
    if (!Number.isFinite(from) || !Number.isFinite(to) || from < 1 || to < from) return;
    await write({
      contract: 'crowdsale',
      fn: 'claimRange',
      args: [BigInt(from), BigInt(to)],
      options: {
        pendingTitle: `Claiming periods ${from}–${to}…`,
        successTitle: `Claimed periods ${from}–${to}`,
        invalidate: [['metha', 'crowdsale'], ['metha', 'token']],
      },
    });
    setRangeFrom('');
    setRangeTo('');
  }

  return (
    <div className="panel mb-14">
      <div className="p-head">
        <h3>Unclaimed periods</h3>
        <span className="src"><code>getAccountNextPeriodWithClaimableTokens · claimPeriod / claimList / claimRange</code></span>
      </div>

      {!account && (
        <div className="p-body" style={{ color: 'var(--dim)', fontSize: 12 }}>
          Connect your wallet to see your unclaimed periods.
        </div>
      )}

      {account && unclaimed.isLoading && (
        <div className="p-body" style={{ color: 'var(--dim)', fontSize: 12 }}>
          Walking your contribution history…
        </div>
      )}

      {account && !unclaimed.isLoading && periods.length === 0 && (
        <div className="p-body" style={{ color: 'var(--dim)', fontSize: 12 }}>
          Nothing unclaimed. Either you've claimed everything or you haven't
          contributed to a closed period yet.
        </div>
      )}

      {account && periods.length > 0 && (
        <>
          {/* Selection toolbar */}
          <div style={{ padding: '10px 18px', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)', background: 'var(--panel-2)', display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'var(--mono)', fontSize: 11 }}>
            <span className="dim" style={{ letterSpacing: 1, textTransform: 'uppercase', fontSize: 10 }}>
              {periods.length} unclaimed · {selected.size} selected
            </span>
            <button className="btn btn-sm" onClick={selectAll} disabled={periods.length === selected.size}>All</button>
            <button className="btn btn-sm" onClick={selectNone} disabled={selected.size === 0}>None</button>
            <span style={{ flex: 1 }} />
            {optimalCall && (
              <span className="dim" style={{ fontSize: 10 }}>
                will call <code style={{ color: 'var(--ctx)' }}>{optimalCall.fn}</code>
              </span>
            )}
            <button
              className={`btn btn-sm ${optimalCall && !submitting ? 'btn-prim' : 'btn-locked'}`}
              disabled={!optimalCall || submitting}
              onClick={doClaimSelected}
            >
              {submitting ? 'Claiming…' : `Claim ${selected.size || 0} selected`}
            </button>
          </div>

          {/* Period table */}
          <div className="tbl">
            <div className="th" style={{ gridTemplateColumns: '40px 80px 1fr 140px 100px' }}>
              <span></span><span>PERIOD</span><span>YOUR CONTRIBUTION</span><span></span><span></span>
            </div>
            {periods.map((id) => (
              <UnclaimedRow
                key={id}
                periodId={id}
                account={account}
                isSelected={selected.has(id)}
                onToggle={() => toggle(id)}
                submitting={submitting}
              />
            ))}
          </div>
        </>
      )}

      {/* Range-claim form — always visible (lets the user blindly call
          claimRange over a contiguous slab without needing it to show up
          in the unclaimed walk above). */}
      <div style={{ padding: '14px 18px', borderTop: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span className="mono dim" style={{ fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase' }}>
          Or claim a range
        </span>
        <input
          className="mono"
          style={{ width: 80, padding: '4px 8px', fontSize: 11, background: 'var(--panel-2)', border: '1px solid var(--line-2)', color: 'var(--ink)' }}
          placeholder="from"
          inputMode="numeric"
          value={rangeFrom}
          onChange={(e) => setRangeFrom(e.target.value.replace(/[^0-9]/g, ''))}
        />
        <span className="dim">→</span>
        <input
          className="mono"
          style={{ width: 80, padding: '4px 8px', fontSize: 11, background: 'var(--panel-2)', border: '1px solid var(--line-2)', color: 'var(--ink)' }}
          placeholder="to"
          inputMode="numeric"
          value={rangeTo}
          onChange={(e) => setRangeTo(e.target.value.replace(/[^0-9]/g, ''))}
        />
        <button
          className={`btn btn-sm ${rangeFrom && rangeTo && account && !submitting ? '' : 'btn-locked'}`}
          disabled={!rangeFrom || !rangeTo || !account || submitting}
          onClick={doClaimRange}
        >
          claimRange
        </button>
        <span className="dim help" style={{ marginLeft: 'auto', fontSize: 10 }}>
          Periods you've already claimed are silently skipped by the contract.
        </span>
      </div>
    </div>
  );
}

/** One row inside the unclaimed-periods table — fetches its own contribution
 *  amount lazily via useWeiContributionPerPeriod, so we don't N+1 the
 *  parent component with explicit useQueries. */
function UnclaimedRow({
  periodId, account, isSelected, onToggle, submitting,
}: {
  periodId: number;
  account: string;
  isSelected: boolean;
  onToggle: () => void;
  submitting: boolean;
}) {
  const contrib = useWeiContributionPerPeriod(periodId, account);
  const { write, status } = useWriteContract();
  const rowSubmitting = status === 'submitting' || status === 'pending';

  function doClaimOne() {
    if (rowSubmitting || submitting) return;
    return write({
      contract: 'crowdsale',
      fn: 'claimPeriod',
      args: [BigInt(periodId)],
      options: {
        pendingTitle: `Claiming period ${periodId}…`,
        successTitle: `Claimed period ${periodId}`,
        invalidate: [['metha', 'crowdsale'], ['metha', 'token']],
      },
    });
  }

  return (
    <div className="tr" style={{ gridTemplateColumns: '40px 80px 1fr 140px 100px', cursor: 'default' }}>
      <span>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          style={{ accentColor: 'var(--ctx)' }}
        />
      </span>
      <span className="pid">P{periodId}</span>
      <span className="who" style={{ color: 'var(--ink)' }}>
        {contrib.data !== undefined ? `${weiToEth(contrib.data).toFixed(4)} ETH` : '…'}
      </span>
      <span className="dim mono" style={{ fontSize: 10 }}>
        period {periodId}
      </span>
      <span>
        <button
          className={`btn btn-sm ${rowSubmitting || submitting ? 'btn-locked' : ''}`}
          disabled={rowSubmitting || submitting}
          onClick={doClaimOne}
        >
          {rowSubmitting ? '…' : 'Claim'}
        </button>
      </span>
    </div>
  );
}

function KpiLink({ to, l, v, d, tone }: {
  to: string; l: string; v: ReactNode; d: ReactNode; tone?: 'up' | 'dn';
}) {
  return (
    <Link to={to} className="kpi" style={{ textDecoration: 'none' }}>
      <span className="l">{l}</span>
      <span className="v">{v}</span>
      <span className={`d${tone ? ' ' + tone : ''}`}>{d}</span>
    </Link>
  );
}
